const {
  getElement,
  getElements,
  getViewPercentage,
  viewportSize,
} = window.BAO.Utils

export class GridSwitcher extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['button']
  }

  constructor () {
    super()

    document.addEventListener(
      'DOMContentLoaded',
      this.handleFiltersOnLoad.bind(this),
    )
  }

  connectedCallback () {
    super.connectedCallback()

    this.loading = false

    // If the collection has editorial is an option, we ignore the saved column values
    const shouldUseEditorial = this.editorialViewEnabled && !this.hidePromos

    this.columnsMobile = shouldUseEditorial
      ? 2
      : Number(this.dataset.columnsMobile)
    this.columnsDesktop = shouldUseEditorial
      ? 4
      : Number(this.dataset.columnsDesktop)

    this.hideLifestyleMobile = this.hidePromos
      ? true
      : shouldUseEditorial
        ? false
        : this.dataset.hideLifestyleMobile === 'true'
    this.hideLifestyleDesktop = this.hidePromos
      ? true
      : shouldUseEditorial
        ? false
        : this.dataset.hideLifestyleDesktop === 'true'
    this.editorialViewActive =
      this.dataset.editorialViewActive === 'true' && !this.hidePromos

    this.filterOptionClick = this.filterOptionClick.bind(this)
  }

  setupListeners () {
    super.setupListeners()

    this.onButtonClick = this.onButtonClick.bind(this)
    this.listeners.add(this.els.button.element, 'click', this.onButtonClick)

    // Grid view button (found in Editorial section)
    this.onGridViewButtonClick = this.onGridViewButtonClick.bind(this)
    this.listeners.add(document.body, 'click', e => {
      const gridViewButton = e.target.closest(
        '[data-grid-switcher-external-el="grid-view"]',
      )
      if (gridViewButton) this.onGridViewButtonClick()
    })

    // Filtering logic
    this.onFilterButtonClick = this.onFilterButtonClick.bind(this)
    this.onFilterResetClick = this.onFilterResetClick.bind(this)

    if (this.externalEls.reset?.exists && this.editorialViewActive) {
      this.listeners.add(
        this.externalEls.reset.element,
        'click',
        this.onFilterResetClick,
      )
    }

    // Detect filters open when in Editorial mode
    if (
      this.externalEls['filter-button']?.element &&
      this.dataset.editorialViewActive === 'true'
    ) {
      this.listeners.add(
        this.externalEls['filter-button'].element,
        'click',
        this.onFilterButtonClick,
      )
    }
  }

  handleFiltersOnLoad () {
    // If the page is loaded in editorial mode, but with active filters in the URL, cycle columns
    if (this.editorialViewActive && this.hasFilterParams) {
      this.cycleColumns()
    }
  }

  onButtonClick () {
    if (this.loading) return

    window.dataLayer.push({
      event: 'gridSwitcherButtonClick',
      button: this.els.button.element.dataset.title,
    })

    this.cycleColumns()
  }

  onGridViewButtonClick () {
    if (this.loading) return

    this.cycleColumns()
  }

  async cycleColumns () {
    this.setLoading()
    this.updateCycles()
    this.viewport = viewportSize() === 'xs' ? 'mobile' : 'desktop'

    this.getInViewCard()

    const currentCycle = this.currentCycle()
    const nextCycle = this.nextCycle()

    if (!currentCycle || !nextCycle) {
      console.warn(
        'Current or next cycle can be found. Please check buildMobileCycles and buildDesktopCycles logic.',
      )
      this.unsetLoading()
      return
    }

    const switchToEditorial = !currentCycle.editorial && nextCycle.editorial
    const switchToGrid = currentCycle.editorial && !nextCycle.editorial
    const cycleGrid = !currentCycle.editorial && !nextCycle.editorial

    const { columns, lifestyle } = nextCycle

    if (switchToEditorial) {
      // If an editorial view is present on the current collection, it will always be the default
      // on load, therefore we don't need to update any values
      await this.switchToEditorial()
    } else if (switchToGrid) {
      // When loading the grid, we set the column storage first so there's no layout shift once loaded
      await this.switchToGrid()
      this.updateProperties(columns, lifestyle)
    } else if (cycleGrid) {
      this.setColumnStorage(columns, lifestyle)
      this.updateProperties(columns, lifestyle)
      this.updateGridStyles(columns, lifestyle)
      this.updateTriggerIcon(columns, lifestyle)
      this.scrollToInViewCard()
    }

    this.unsetLoading()

    if (switchToEditorial || switchToGrid) {
      window.BAO.dispatchEvent(window.BAO.EVENTS.GRID_SWITCHER.SWITCHED_LAYOUT)
    } else {
      window.BAO.dispatchEvent(window.BAO.EVENTS.GRID_SWITCHER.SWITCHED_COLUMNS)
    }
  }

  updateProperties (columns, lifestyle) {
    if (this.viewport === 'mobile') {
      this.columnsMobile = Number(columns)

      if (this.lifestyleViewEnabled) {
        this.hideLifestyleMobile = !lifestyle
      }
    }
    if (this.viewport === 'desktop') {
      this.columnsDesktop = Number(columns)

      if (this.lifestyleViewEnabled) {
        this.hideLifestyleDesktop = !lifestyle
      }
    }
  }

  updateGridStyles (columns, lifestyle) {
    const columnStyleVariable =
      this.viewport === 'mobile'
        ? '--Grid_ColumnsMobile'
        : '--Grid_ColumnsDesktop'
    const columnAttribute =
      this.viewport === 'mobile'
        ? 'data-columns-mobile'
        : 'data-columns-desktop'
    const hideLifestyleAttribute =
      this.viewport === 'mobile'
        ? 'data-hide-lifestyle-mobile'
        : 'data-hide-lifestyle-desktop'

    // Update to use desktop and mobile independently
    this.prdListElem?.style.setProperty(columnStyleVariable, Number(columns))
    this.prdListElem?.setAttribute(columnAttribute, Number(columns))
    this.prdListElem?.setAttribute(hideLifestyleAttribute, !lifestyle)
  }

  updateTriggerIcon (columns, lifestyle) {
    this.els.icon?.elements.forEach(icon => {
      const iconLifestyle = icon.dataset.lifestyle === 'true'

      if (icon.dataset.viewport === this.viewport) {
        if (
          Number(icon.dataset.columns) === Number(columns) &&
          iconLifestyle === lifestyle
        ) {
          icon.setAttribute('aria-hidden', false)
        } else {
          icon.setAttribute('aria-hidden', true)
        }
      }
    })
  }

  async setColumnStorage (value, lifestyle) {
    // We don't need to store the layout in Editorial as the editorial template
    // should always display on page load, followed by the default cycles
    if (this.editorialViewEnabled) return

    const columnStorageName =
      this.viewport === 'mobile'
        ? this.storageNameColumnsMobile
        : this.storageNameColumnsDesktop
    const hideLifestyleStorageName =
      this.viewport === 'mobile'
        ? this.storageNameHideLifestyleMobile
        : this.storageNameHideLifestyleDesktop

    const attributes = {
      [columnStorageName]: Number(value),
    }

    if (this.lifestyleViewEnabled) {
      attributes[hideLifestyleStorageName] = !lifestyle
    }

    // eslint-disable-next-line compat/compat
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes }),
    })
  }

  getInViewCard () {
    const listItemsArray = getElements('.prd-List_Items > .prd-List_Item')

    const offsetTopArray = listItemsArray.map(listItem => {
      return listItem.getBoundingClientRect().top
    })

    const closestToTop = this.closestToZero(offsetTopArray)
    this.closestCardIndex = offsetTopArray.indexOf(closestToTop)
  }

  scrollToInViewCard () {
    if (!this.closestCardIndex) return

    const listItems = document.querySelectorAll(
      '.prd-List_Items > .prd-List_Item',
    )
    const topListItem = listItems[this.closestCardIndex]

    if (!topListItem) return

    if (getViewPercentage(topListItem) < 70) {
      topListItem.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  closestToZero (numbers) {
    if (!numbers.length) {
      return 0
    }

    let closest = 0

    for (let i = 0; i < numbers.length; i++) {
      if (closest === 0) {
        closest = numbers[i]
      } else if (numbers[i] === 0) {
        closest = numbers[i]
      } else if (numbers[i] > 0 && numbers[i] <= Math.abs(closest)) {
        closest = numbers[i]
      } else if (numbers[i] < 0 && -numbers[i] < Math.abs(closest)) {
        closest = numbers[i]
      }
    }

    return closest
  }

  currentCycle () {
    const cycles =
      this.viewport === 'mobile' ? this.mobileCycles : this.desktopCycles
    return cycles.find(cycle => cycle.active)
  }

  nextCycle () {
    const cycles =
      this.viewport === 'mobile' ? this.mobileCycles : this.desktopCycles
    const currentIndex = cycles.findIndex(cycle => cycle.active)

    let nextIndex = currentIndex
    do {
      nextIndex = (nextIndex + 1) % cycles.length
    } while (
      this.hasFilterParams &&
      cycles[nextIndex].lifestyle === true &&
      nextIndex !== currentIndex
    )

    return cycles[nextIndex]
  }

  async switchToGrid () {
    window.scrollTo(0, 0)

    this.editorialViewActive = false

    await this.loadGrid()
  }

  async switchToEditorial () {
    window.scrollTo(0, 0)

    this.editorialViewActive = true

    await this.loadEditorial()
  }

  async loadEditorial () {
    // Cache the DOM so we don't fetch every cycle.
    if (!this.newEditorialDOM) {
      // Leave the section ID out, since we can't always know it.
      const fetchSection = `&section=`
      const fetchContext = `/collections/${this.collectionHandle}?view=${this.gridSwitcherTemplateName}`

      const section = await fetch(`${fetchContext}${fetchSection}`)
      const HTMLText = await section.text()
      this.newEditorialDOM = window.BAO.sectionRenderer.convertToDOM(HTMLText)
    }

    const editorialEl = this.newEditorialDOM.querySelector(
      '[data-editorial-view-section]',
    )

    if (!editorialEl) {
      // eslint-disable-next-line no-console
      console.warn(
        'The editorial view could not be found. Please make sure the template name is correct.',
      )
      return
    }

    this.externalEls.section.element.outerHTML = editorialEl.outerHTML
  }

  async loadGrid () {
    if (!this.gridSwitcherTemplateName) {
      // eslint-disable-next-line no-console
      console.log(
        'No grid template name was found. Please check collection.metafields.editorial_view.template_name',
      )
      return
    }

    // Get filter params from the URL.
    const splitUrl = window.location.search.split('?')
    const urlParams = splitUrl && splitUrl[1] ? `&${splitUrl[1]}` : ''

    if (!this.newGridDOM) {
      // Leave the section ID out, since we can't always know it. Load the whole
      // page and grab the section by a data attribute.
      const fetchSection = `&section=`
      const fetchContext = `/collections/${this.collectionHandle}?view=collection${this.gridPageNumber}${urlParams}`

      const section = await fetch(`${fetchContext}${fetchSection}`)
      const HTMLText = await section.text()

      this.newGridDOM = window.BAO.sectionRenderer.convertToDOM(HTMLText)
    }

    const gridEl = this.newGridDOM.querySelector('[data-grid-view-section]')
    this.externalEls.section.element.outerHTML = gridEl.outerHTML
  }

  // This is a stand-in for the filter reset button in editorial mode.
  // Should be an edge case, will happen when an editorial PLP is loaded with filter params.
  onFilterResetClick (e) {
    e.stopImmediatePropagation()

    this.updateURLHash(true)

    // Reload the page with no filters.
    location.reload(true)
  }

  onFilterButtonClick () {
    this.listeners.add(document, 'click', this.filterOptionClick)
  }

  filterOptionClick (e) {
    const filterOption = e.target.closest('[data-filter-option]')

    // If click came from inside the collection filters drawer, change to grid and apply filters.
    if (filterOption) {
      e.preventDefault()
      e.stopImmediatePropagation()

      // Update URL with filter params.
      this.updateURLHash()

      // Swap to grid mode, this will use the new filters.
      this.cycleColumns()

      this.listeners.remove('click', this.filterOptionClick)
    }
  }

  updateURLHash (clear = false) {
    let searchParams = ''

    if (!clear) {
      const filterForm = getElement('form[is="collection-filters-form"]')
      const formData = new FormData(filterForm)
      // eslint-disable-next-line compat/compat
      searchParams = new URLSearchParams(formData).toString()
    }

    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  buildMobileCycles () {
    const cycles = []

    // Always include lifestyle state if lifestyle is enabled (regardless of hideLifestyle)
    // But exclude if data-hide-promos exists
    if (
      this.lifestyleViewEnabled &&
      !this.hasFilterParams &&
      !this.hidePromos
    ) {
      cycles.push({
        columns: 2,
        lifestyle: true,
        editorial: false,
        active:
          !this.editorialViewActive &&
          !this.hideLifestyleMobile &&
          this.columnsMobile === 2,
      })
    }

    // Default cycles
    cycles.push(
      {
        columns: 2,
        lifestyle: false,
        editorial: false,
        active:
          !this.editorialViewActive &&
          (this.hideLifestyleMobile ||
            this.hasFilterParams ||
            !this.lifestyleViewEnabled) &&
          this.columnsMobile === 2,
      },
      {
        columns: 1,
        lifestyle: false,
        editorial: false,
        active: !this.editorialViewActive && this.columnsMobile === 1,
      },
    )

    // Include editorial view if enabled and data-hide-promos is not present
    if (this.editorialViewEnabled && !this.hidePromos) {
      cycles.push({
        columns: null,
        lifestyle: false,
        editorial: true,
        active: this.editorialViewActive,
      })
    }

    return cycles
  }

  buildDesktopCycles () {
    const cycles = []

    // Always include lifestyle state if enabled (even if hidden)
    // But exclude if data-hide-promos exists
    if (
      this.lifestyleViewEnabled &&
      !this.hasFilterParams &&
      !this.hidePromos
    ) {
      cycles.push({
        columns: 4,
        lifestyle: true,
        editorial: false,
        active:
          !this.editorialViewActive &&
          !this.hideLifestyleDesktop &&
          this.columnsDesktop === 4,
      })
    }

    // Default cycles
    cycles.push(
      {
        columns: 4,
        lifestyle: false,
        editorial: false,
        active:
          !this.editorialViewActive &&
          (this.hideLifestyleDesktop ||
            this.hasFilterParams ||
            !this.lifestyleViewEnabled) &&
          this.columnsDesktop === 4,
      },
      {
        columns: 6,
        lifestyle: false,
        editorial: false,
        active: !this.editorialViewActive && this.columnsDesktop === 6,
      },
    )

    // Include editorial view if enabled and data-hide-promos is not present
    if (this.editorialViewEnabled && !this.hidePromos) {
      cycles.push({
        columns: null,
        lifestyle: false,
        editorial: true,
        active: this.editorialViewActive,
      })
    }

    return cycles
  }

  updateCycles () {
    this._mobileCycles = this.buildMobileCycles()
    this._desktopCycles = this.buildDesktopCycles()
  }

  setLoading () {
    this.loading = true

    this.externalEls.section.element.setAttribute('aria-busy', 'true')

    this.els.button.element.setAttribute('disabled', '')
  }

  unsetLoading () {
    this.loading = false

    this.externalEls.section.element.setAttribute('aria-busy', 'false')

    this.els.button.element.removeAttribute('disabled')
  }

  get hasFilterParams () {
    const params = new URLSearchParams(window.location.search)
    for (const key of params.keys()) {
      if (key.startsWith('filter.')) {
        return true
      }
    }
    return false
  }

  get storageNameColumnsDesktop () {
    return `grid_columns_desktop`
  }

  get storageNameColumnsMobile () {
    return `grid_columns_mobile`
  }

  get storageNameHideLifestyleMobile () {
    return `grid_hide_lifestyle_mobile`
  }

  get storageNameHideLifestyleDesktop () {
    return `grid_hide_lifestyle_desktop`
  }

  get prdListElem () {
    return getElement('[data-prd-list-clc]')
  }

  get mobileCycles () {
    return this._mobileCycles
  }

  get desktopCycles () {
    return this._desktopCycles
  }

  get editorialViewEnabled () {
    return this.dataset.editorialViewEnabled === 'true'
  }

  get hidePromos () {
    return this.externalEls.section?.element?.hasAttribute('data-hide-promos')
  }

  get lifestyleViewEnabled () {
    return this.dataset.lifestyleViewEnabled === 'true'
  }

  get gridSwitcherTemplateName () {
    return this.dataset.gridSwitcherTemplateName
  }

  get collectionHandle () {
    return this.dataset.collectionHandle
  }

  get gridPageNumber () {
    // eslint-disable-next-line compat/compat
    const urlParams = new URLSearchParams(window.location.search)
    const pageNumber = urlParams.get('page')
    let pageQuery = ''

    if (pageNumber) {
      pageQuery = `&page=${pageNumber}`
    }

    return pageQuery
  }
}

if (!customElements.get('grid-switcher')) {
  customElements.define('grid-switcher', GridSwitcher)
}
